'use client';

import { useEffect, useState } from 'react';
import { FeatherAlertTriangle } from '@subframe/core';
import { Button, Dialog, TextField } from '@/shared/components/ui';
import { feriadosService } from '@/features/feriados/services/feriadosService';
import { type SimulacaoPrazo } from '@/features/feriados/types/feriadosTypes';
interface SimularPrazoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfigurarAno: (ano: number) => void;
}
function formatarDataBR(iso: string): string {
  const [ano, mes, dia] = iso.split('-');
  if (!ano || !mes || !dia) return iso;
  return `${dia}/${mes}/${ano}`;
}
function motivoLabel(dia: SimulacaoPrazo['diasPulados'][number]): string {
  if (dia.motivo === 'WEEKEND') return 'Fim de semana';
  return dia.nomeFeriado ? `Feriado: ${dia.nomeFeriado}` : 'Feriado';
}
export function SimularPrazoDialog({
  open,
  onOpenChange,
  onConfigurarAno
}: SimularPrazoDialogProps) {
  const [dataInicial, setDataInicial] = useState('');
  const [diasUteis, setDiasUteis] = useState('');
  const [resultado, setResultado] = useState<SimulacaoPrazo | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [calculando, setCalculando] = useState(false);
  useEffect(() => {
    if (!open) return;
    setDataInicial('');
    setDiasUteis('');
    setResultado(null);
    setErro(null);
  }, [open]);
  const handleCalcular = async () => {
    const dias = Number(diasUteis);
    if (!dataInicial) {
      setErro('Informe a data inicial.');
      setResultado(null);
      return;
    }
    if (!Number.isInteger(dias) || dias <= 0) {
      setErro('Informe uma quantidade de dias úteis maior que zero.');
      setResultado(null);
      return;
    }
    setErro(null);
    setCalculando(true);
    try {
      const simulacao = await feriadosService.simularPrazo({
        dataInicial,
        diasUteis: dias
      });
      setResultado(simulacao);
    } catch (error) {
      setErro(error instanceof Error ? error.message : 'Não foi possível calcular.');
      setResultado(null);
    } finally {
      setCalculando(false);
    }
  };
  return <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className="w-[520px] max-w-[95vw]">
        <div className="flex w-full flex-col gap-4 p-6">
          <div className="flex flex-col">
            <span className="text-heading-3 font-heading-3 text-default-font">
              Simular cálculo de dias úteis
            </span>
            <span className="text-caption font-caption text-subtext-color">
              Confira a data de vencimento antes de gerar o prazo de verdade.
            </span>
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <TextField className="flex-1" label="Data inicial">
              <TextField.Input type="date" value={dataInicial} onChange={event => setDataInicial(event.target.value)} />
            </TextField>
            <TextField className="w-40" label="Dias úteis">
              <TextField.Input type="number" value={diasUteis} placeholder="ex.: 15" onChange={event_0 => setDiasUteis(event_0.target.value)} />
            </TextField>
            <Button variant="brand-primary" loading={calculando} onClick={() => void handleCalcular()}>
              Calcular
            </Button>
          </div>

          {erro ? <span className="text-caption font-caption text-error-700">{erro}</span> : null}

          {resultado && resultado.anoSemCalendario !== null ? <div className="border-error-200 bg-error-50 flex flex-col gap-2 rounded-[8px] border border-solid p-3">
              <div className="flex items-center gap-2">
                <FeatherAlertTriangle className="text-error-700 h-4 w-4" />
                <span className="text-body-bold font-body-bold text-error-700">
                  Cálculo recusado
                </span>
              </div>
              <span className="text-caption font-caption text-error-700">
                O ano {resultado.anoSemCalendario} ainda não tem calendário configurado. Configure-o
                para calcular prazos que vencem nesse ano.
              </span>
              <div>
                <Button variant="neutral-secondary" size="small" onClick={() => {
              onConfigurarAno(resultado.anoSemCalendario as number);
              onOpenChange(false);
            }}>
                  Configurar {resultado.anoSemCalendario}
                </Button>
              </div>
            </div> : null}

          {resultado && resultado.dataFinal ? <div className="flex flex-col gap-3">
              <div className="border-success-200 bg-success-50 flex flex-col gap-1 rounded-[8px] border border-solid p-3">
                <span className="text-caption font-caption text-subtext-color">
                  Data de vencimento
                </span>
                <span className="text-heading-3 font-heading-3 text-success-700">
                  {formatarDataBR(resultado.dataFinal)}
                </span>
              </div>

              <div className="flex flex-col gap-1">
                <span className="text-caption-bold font-caption-bold text-default-font">
                  Dias considerados ({resultado.diasPulados.length} pulado
                  {resultado.diasPulados.length === 1 ? '' : 's'})
                </span>
                {resultado.diasPulados.length === 0 ? <span className="text-caption font-caption text-subtext-color">
                    Nenhum dia pulado no período.
                  </span> : <div className="flex max-h-48 flex-col gap-1 overflow-y-auto">
                    {resultado.diasPulados.map(dia => <div key={dia.data} className="flex items-center justify-between rounded-md px-2 py-1 odd:bg-neutral-50">
                        <span className="text-caption font-caption text-default-font tabular-nums">
                          {formatarDataBR(dia.data)}
                        </span>
                        <span className="text-caption font-caption text-subtext-color">
                          {motivoLabel(dia)}
                        </span>
                      </div>)}
                  </div>}
              </div>
            </div> : null}

          <div className="flex justify-end">
            <Button variant="neutral-secondary" onClick={() => onOpenChange(false)}>
              Fechar
            </Button>
          </div>
        </div>
      </Dialog.Content>
    </Dialog>;
}